/**
 * @stefano / 04-05-2020
 * Questo componente viene usato anche nel riepilogo per il pagamento.
 * E' stato chiesto che venga aggiunta una voce nel riepilogo per il pagamento da aggiungere al totale da mostrare, se
 * l'utente ha scelto di non pagare nesun acconto.
 * Quindi qui sotto includo AppContext e le le configs per vedere se sono nel riepilogo e se si aggiungo una cifra al
 * totale e al riepilogo
 * */
import React, { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import Alert from "@material-ui/lab/Alert";
import clsx from "clsx";
// aggiungo AppContext e PaymentContext e diversi configs
// import {
//   participationsLookup,
//   aggiungiAccontoForzatoPrice,
//   paymentsINPS,
// } from "../../main/specificUtils/configs";
import { AppContext } from "../../contexts/AppContext";
import { PaymentContext } from "contexts/PaymentContext";
import { PackageSelectorContext } from "../../contexts/PackageSelectorContext";
import { SpendingContext } from "contexts/SpendingContext";
import BundleResume from "./BundleResume";
import ServiceResume from "./ServiceResume";

function getPriceDesc(price) {
  return price === 0 ? "-" : `€${price}`;
}

const useStyles = makeStyles((theme) => ({
  progress: {
    margin: theme.spacing(2),
  },
  alert: {
    textAlign: "left",
    marginTop: "16px",
  },
  list: {
    margin: 0,
    padding: 0,
    listStyle: "none",
    width: "100%",
    textAlign: "left",
  },
  border: {
    borderTop: "1px solid rgba(0, 0, 0, 0.1)",
    marginTop: "1.5rem",
    paddingTop: "1.5rem",
  },
  label: {
    color: "#676f7e",
    marginBottom: "8px",
    fontSize: "14px",
  },
  listItem: {
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
    fontSize: "14px",
  },
  price: {
    fontSize: "20px",
    color: "#21242c",
    fontWeight: 600,
  },
}));

export default function Price(props) {
  const classes = useStyles();
  // @stefano / 04-05-2020: setto appState e setto paymentState
  const [appState] = useContext(AppContext);
  const [paymentState] = useContext(PaymentContext);
  const [packageState] = useContext(PackageSelectorContext);
  const [spendingState] = useContext(SpendingContext);
  const { hasExtraPaymentServices, isVisualPrice } = paymentState;

  const {
    isLoadingPrice,
    priceToPay,
    totalPrice,
    visualTotalPrice,
    packageId,
    basicCorePrice,
    departureService,
    departureCityLabel,
    departureServicePrice,
    structure,
    structureLabel,
    structurePrice,
    programTypology,
    programTypologyLabel,
    programTypologyPrice,
    contributoInpsLabel,
    bonusValueLabel,
    bonusLabel,
    extraServiceLabels,
    basicCoreCluster,
    step2Coupon,
    selectedBundles,
    depositPrice,
  } = props;

  const { voucherResiduo, approximatePrice } = useMemo(() => {
    let voucherResiduo = 0;
    let approximatePrice = false;

    if (spendingState.vouchers) {
      voucherResiduo = spendingState.vouchers.reduce(function (
        accumulator,
        voucher
      ) {
        return accumulator +
          (voucher.paymentMethodResidual - voucher.paymentMethodConsumed) +
          voucher.canApplyDiscount
          ? voucher.valueDiscountResidual - voucher.valueDiscountConsumed
          : 0;
      },
      0);
    }

    const pack =
      packageState.packages &&
      packageState.packages.find((p) => p.value === packageState.package);

    if (pack) {
      approximatePrice = pack.approximatePrice === true;
    }

    return {
      voucherResiduo,
      approximatePrice,
    };
  }, [spendingState.vouchers, packageState.packages, packageState.package]);

  return (
    <React.Fragment>
      {!!basicCoreCluster && (
        <div className={classes.alert}>
          <Alert severity="info" color="success">
            Il tuo livello di appartenenza al GiocamondoClub è "
            {basicCoreCluster.name}"
          </Alert>
        </div>
      )}
      <ul className={classes.list}>
        {packageId != null && (
          <ServiceResume
            title="Prezzo pacchetto"
            price={getPriceDesc(basicCorePrice)}
          />
        )}
        {departureService != null && (
          <ServiceResume
            title={departureCityLabel}
            price={getPriceDesc(departureServicePrice)}
          />
        )}
        {Boolean(structure) && (
          <ServiceResume
            title="Selezione"
            price={`${structureLabel} €${structurePrice}`}
          />
        )}
        {!!programTypology && (
          <ServiceResume
            title={programTypologyLabel}
            price={getPriceDesc(programTypologyPrice)}
          />
        )}
        {Boolean(contributoInpsLabel) && (
          <ServiceResume title="Contributo inps" price={contributoInpsLabel} />
        )}
        {Boolean(bonusValueLabel) && (
          <ServiceResume title={bonusLabel} price={bonusValueLabel} />
        )}
        {!!spendingState.vouchers &&
          spendingState.vouchers.map((voucher) => (
            <>
              {voucher.paymentMethodConsumed > 0 && (
                <ServiceResume
                  title={voucher.voucherDescription}
                  price={`€ -${voucher.paymentMethodConsumed / 100}`}
                />
              )}
              {voucher.valueDiscountConsumed > 0 && (
                <ServiceResume
                  title={voucher.discountDescription}
                  price={`€ -${voucher.valueDiscountConsumed / 100}`}
                />
              )}
              {voucher.percentDiscountConsumed > 0 && (
                <ServiceResume
                  title={voucher.discountDescription}
                  price={`(${voucher.discountPercent}%) € -${
                    voucher.percentDiscountConsumed / 100
                  }`}
                />
              )}
            </>
          ))}
        {voucherResiduo > 0 && (
          <ServiceResume title="Può ancora usare {voucherResiduo / 100} € del credito dei suoi voucher." />
        )}
        {!!spendingState.helperMinusValue && (
          <ServiceResume
            title={spendingState.helperMinusDescription}
            price={`€ ${spendingState.helperMinusValue / 100}`}
          />
        )}
        {!!spendingState.helperPlusValue && (
          <ServiceResume
            title={spendingState.helperPlusDescription}
            price={`€ ${spendingState.helperPlusValue / 100}`}
          />
        )}
        {selectedBundles.map((b, i) => (
          <BundleResume key={`selected-bundle-${b.code}-${i}`} bundle={b} />
        ))}
        {extraServiceLabels.length > 0 && (
          <p className={classes.label}>Servizi Selezionati</p>
        )}
        {extraServiceLabels.map((es, i) => (
          <ServiceResume
            key={`summary-${es.id}-${i}`}
            title={es.title}
            subtitle={es.realvalue > 0 ? es.value : es.label}
            price={es.isVisualPrice ? "**" : undefined}
          />
        ))}
        {Boolean(bonusValueLabel) && (
          <li>
            <small className="text-small text-danger">
              <span className="text-alert">*</span>
              La scontistica sarà subordinata alla verifica da parte della
              Giocamondo delle informazioni da lei inserite in fase di
              prenotazione.
            </small>
            {/* <small className="text-small text-danger">
              <span className="text-alert">*</span>
              La scontistica applicata sarà subordinata alla verifica delle
              informazioni inserite. La Giocamondo si riserva, in ogni caso, anche
              successivamente all'uscita delle graduatorie (se partecipante al
              bando INPSieme) di adeguare la pratica qualora non si verificassero
              le condizioni necessarie per usufruire del bonus.
              <strong>
                NB: Anche successivamente all’uscita delle graduatorie.
              </strong>
            </small> */}
          </li>
        )}

        {isVisualPrice && (
          <li>
            <small className="text-small text-danger" style={{ marginTop: 8 }}>
              <span className="text-alert">**</span>
              Il costo del servizio non incide sul saldo della pratica, ma verrà
              venduto separatamente
            </small>
          </li>
        )}
        {Boolean(step2Coupon) && (
          <li>
            <small className="text-small text-danger">
              <span className="text-alert">*</span>
              La Giocamondo si riserva di controllare la correttezza del dato
              inserito
            </small>
            {/* <small className="text-small text-danger">
              <span className="text-alert">*</span>
              La scontistica applicata sarà subordinata alla verifica delle
              informazioni inserite. La Giocamondo si riserva, in ogni caso, anche
              successivamente all'uscita delle graduatorie (se partecipante al
              bando INPSieme) di adeguare la pratica qualora non si verificassero
              le condizioni necessarie per usufruire del bonus.
              <strong>
                NB: Anche successivamente all’uscita delle graduatorie.
              </strong>
            </small> */}
          </li>
        )}
      </ul>

      {props.children}
      <ul className={`${classes.list} ${classes.border}`}>
        {visualTotalPrice && (
          <>
            <li className={classes.listItem}>
              <span className={classes.price}>
                Costo al netto del contributo rimborsato da INPS
              </span>
              {isLoadingPrice ? (
                <CircularProgress
                  className={clsx(classes.progress, "progress-size")}
                />
              ) : (
                <span className={classes.price}>
                  €{visualTotalPrice < 0 ? 0 : visualTotalPrice / 100}
                </span>
              )}
            </li>
            <br />
          </>
        )}
        {depositPrice > 0 && (
          <>
            <li className={classes.listItem}>
              <span className={classes.price}>Acconto</span>
              <span className={classes.price}>€{depositPrice / 100}</span>
            </li>
            <br />
          </>
        )}
        <li className={classes.listItem}>
          <span className={classes.price}>Totale</span>
          {isLoadingPrice ? (
            <CircularProgress
              className={clsx(classes.progress, "progress-size")}
            />
          ) : (
            <span className={classes.price}>
              €{totalPrice < 0 ? 0 : totalPrice / 100}
            </span>
          )}
        </li>
      </ul>
      {approximatePrice &&
        appState.activeStep >= 4 &&
        appState.step2Participation === "2" && (
          <Alert severity="warning" className={classes.alert}>
            Nel ringraziarti per la richiesta di preventivo che stai inviando ai
            nostri uffici, ti ricordiamo che per i soggiorni individuali la
            disponibilità della camera viene verificata con la struttura
            alberghiera nel momento in cui il cliente manifesta la volontà di
            confermare la sua vacanza. Se vuoi attendere l'uscita delle
            graduatorie INPS, la tua prenotazione verrà lavorata dal nostro
            ufficio all'uscita delle graduatorie se risulterai vincitore del
            contributo. Se intendi partire a prescindere dal contributo INPS, ti
            consigliamo di confermare subito la tua prenotazione selezionando di
            seguito "Si, Parto Sicuro". In questo caso i nostri uffici
            lavoreranno subito la tua richiesta verificando la disponibilità
            della camera.
          </Alert>
        )}
    </React.Fragment>
  );
}

Price.propTypes = {
  isLoadingPrice: PropTypes.bool.isRequired,
  priceToPay: PropTypes.number.isRequired,
  visualTotalPrice: PropTypes.number.isRequired,
  packageId: PropTypes.any,
  basicCorePrice: PropTypes.number.isRequired,
  departureService: PropTypes.any,
  departureCityLabel: PropTypes.string.isRequired,
  departureServicePrice: PropTypes.number.isRequired,
  structure: PropTypes.any,
  structureLabel: PropTypes.string.isRequired,
  structurePrice: PropTypes.number.isRequired,
  programTypology: PropTypes.any,
  programTypologyLabel: PropTypes.string.isRequired,
  programTypologyPrice: PropTypes.number.isRequired,
  contributoInpsLabel: PropTypes.string.isRequired,
  bonusValueLabel: PropTypes.string.isRequired,
  bonusLabel: PropTypes.string.isRequired,
  extraServiceLabels: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  basicCoreCluster: PropTypes.shape({
    price: PropTypes.number,
    name: PropTypes.string,
    code: PropTypes.string,
  }),
};
